import { bound } from "@frui.ts/helpers/dist";
import { Router, ScreenBase, watchBusy } from "@frui.ts/screens/dist";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation/dist";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import { Modules } from "config";
import type Role from "entities/role";
import User from "entities/user";
import UserRole from "entities/userRole";
import RoleRepository from "repositories/roleRepository";
import UserRepository from "repositories/userRepository";
import ConfirmationService from "services/confirmationService";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";

export const rolesNavigationName = "roles";

const UserRolesValidationRules: any = {
  role: {
    required: true,
  },
};

@Router.registerRoute({ route: rolesNavigationName, name: Router.Self })
export default class UserRolesViewModel extends ScreenBase {
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable items: UserRole[] = [];
  @observable roles: Role[] = [];
  @observable role: Role | null = null;

  constructor(
    public user: User,
    private userRepository: UserRepository,
    private roleRepository: RoleRepository,
    private localizationService: LocalizationService,
    private confirmationService: ConfirmationService,
    private notificationService: NotificationService,
    private securityService: SecurityService
  ) {
    super();
    this.navigationName = rolesNavigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @watchBusy
  protected async onInitialize() {
    const [userRoles, roles] = await Promise.all([
      this.userRepository.getAllRoles(this.user.id),
      this.roleRepository.getAll({ offset: 0, limit: 100 }),
    ]);
    this.items = userRoles.items;
    this.roles = roles.items;

    attachAutomaticValidator(this, UserRolesValidationRules);
    return super.onInitialize();
  }

  @computed
  get allowWrite() {
    return this.securityService.allowToEntity("userRole", "write");
  }

  @bound
  @watchBusy
  async refreshItems() {
    this.items = (await this.userRepository.getAllRoles(this.user.id)).items;
  }

  @bound
  async onDelete(item: UserRole) {
    try {
      const confirmed = await this.confirmationService.deleteConfirmation();

      if (!confirmed) {
        return;
      }

      await this.userRepository.deleteRole(this.user, item);
      this.refreshItems();
      this.notificationService.add({ title: this.translate("deleted"), type: NotificationType.SUCCESS });
    } catch (e) {}
  }

  @bound
  async onAddClick() {
    try {
      if (validate(this) && this.role) {
        await this.userRepository.addRole(this.user, this.role.id);
        this.refreshItems();
        this.role = null;
        hideValidationErrors(this);
        this.notificationService.add({ title: this.translate("created"), type: NotificationType.SUCCESS });
      }
    } catch (e) {}
  }

  @computed
  get allowedRoles() {
    return this.roles.filter(role => this.items.findIndex(item => item.role.id === role.id) === -1);
  }

  @computed
  get possibleToAddNewRole() {
    return this.allowedRoles.length !== 0;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `users.userRoles.${code}`, size);
  }

  translateAttribute(model: string, attribute: string, size?: number) {
    return this.localizationService.translateAttribute(model, attribute, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (user: User) => {
      return new UserRolesViewModel(
        user,
        container.get(UserRepository),
        container.get(RoleRepository),
        container.get(LocalizationService),
        container.get(ConfirmationService),
        container.get(NotificationService),
        container.get(SecurityService)
      );
    };
  }
}
